import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { forbidExtraProps } from 'airbnb-prop-types';

import { useLocation, useNavigate } from 'react-router';

import { connect } from 'react-redux';

import get from 'lodash/fp/get';
import map from 'lodash/fp/map';

import { legacyTheme, styled } from '@prose-ui/legacy';

import { BasicModal } from 'Components/BasicModal';
import { GoBackButton } from 'Components/GoBackButton';
import { Button } from 'Components/LegacyButton';
import { MetaData } from 'Components/MetaData';
import SceneContainer from 'Components/SceneContainer';
import { Typography } from 'Components/Typography';

import * as statuses from 'constants/statuses';

import Loading from 'Blocks/Loading';

import { LegacyBaseScene as BaseScene } from 'Scenes/LegacyBaseScene';

import * as productsHaircare from 'assets/content/haircareProducts';
import * as productsSkincare from 'assets/content/skincareProducts';

import * as membershipActions from 'dux/membership/actions';
import * as membershipSelectors from 'dux/membership/selectors';
import { useFreeProductCatalog } from 'dux/membership/hooks';

/**
 * Product
 */
const products = { ...productsHaircare, ...productsSkincare };
// All styles are from src/Apps/Account/Scenes/Subscription.js and meant to be shared but using styled api
const ProductSelectionSceneContainer = styled(SceneContainer)`
  padding: ${legacyTheme.spacing.s24};

  text-align: center;

  & button {
    margin-bottom: ${legacyTheme.spacing.s24};
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => (props.gridSize ? `repeat(3, 1fr)` : `repeat(4, 1fr)`)};
  grid-gap: 27px;
  justify-items: center;
  margin: 24px auto;

  text-align: center;

  ${legacyTheme.breakpoints.match('md')} {
    grid-template-columns: repeat(3, 1fr);
  }

  ${legacyTheme.breakpoints.match('sm')} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ProductWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 150px;
`;

const ProductImageWrapper = styled.div`
  min-height: 77px;
`;

const Product = ({ slug, recommended, onClick }) => (
  <ProductWrapper data-testid={get(`${slug}.slug`, products)}>
    <ProductImageWrapper>
      <img
        alt={get(`${slug}.label`, products)}
        src={get(`${slug}.picture`, products).src}
        width={55}
      />
    </ProductImageWrapper>

    <Typography variant="p2">{get(`${slug}.label`, products)}</Typography>

    <Typography paragraph variant="mono3">
      {recommended ? 'Recommended For you' : <>&nbsp;</>}
    </Typography>

    <Button
      data-click="complimentary-select-product"
      data-from={`${slug}-card`}
      data-testid={`select-${slug}`}
      fullWidth
      onClick={onClick}
      variant="add"
    >
      SELECT
    </Button>
  </ProductWrapper>
);

Product.propTypes = {
  onClick: PropTypes.func.isRequired,
  recommended: PropTypes.bool,
  slug: PropTypes.oneOf(Object.keys(products)).isRequired,
};

Product.defaultProps = {
  recommended: false,
};

/**
 * Modal Content
 */

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const ModalContentImage = styled.img`
  width: 55px;
  margin-top: 36px;
`;

const ModalContent = ({ slug, onClick, status }) =>
  slug && (
    <ModalContentWrapper>
      <Typography align="center" paragraph variant="h2">
        Confirm your selection
      </Typography>

      <Typography align="center" paragraph variant="p2">
        You’ve chosen this product to ship free in your next {get(`${slug}.category`, products)}{' '}
        subscription order—it won’t be added to your plan or charged. Good with that?
      </Typography>

      <ModalContentImage
        alt={get(`${slug}.label`, products)}
        src={get(`${slug}.picture`, products).src}
      />

      <br />

      <Typography paragraph variant="mono3">
        your complimentary product:
      </Typography>

      <Typography>{get(`${slug}.label`, products)}</Typography>

      <br />

      <Button
        data-click="complimentary-confirm-product"
        data-from="complimentary-product"
        data-testid={`confirm-${slug}`}
        disabled={status === statuses.LOADING}
        fullWidth
        isLoading={status === statuses.LOADING}
        onClick={onClick}
        variant="vert"
      >
        Confirm Choice
      </Button>
    </ModalContentWrapper>
  );

ModalContent.propTypes = {
  status: PropTypes.oneOf([statuses.LOADING, statuses.SUCCESS, statuses.ERROR]).isRequired,
  onClick: PropTypes.func.isRequired,
  slug: PropTypes.oneOf(Object.keys(products)),
};

ModalContent.defaultProps = {
  slug: null,
};

const ConnectedModalContent = connect(state => ({
  status: membershipSelectors.getMembershipStatus(state),
}))(ModalContent);

/**
 * Product Selection
 */

const ProductSelection = ({ status, canSelectFreeProduct, setSelectedProduct }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedSlug, setSelectedSlug] = useState(null);
  const selectableProducts = useFreeProductCatalog();
  const gridSize = selectableProducts.length < 4;

  useEffect(() => {
    if ([statuses.SUCCESS, statuses.ERROR].includes(status) && !canSelectFreeProduct) {
      navigate('/the-salon');
    }
  }, [status, canSelectFreeProduct]);

  if ([statuses.SUCCESS, statuses.ERROR].includes(status) && !canSelectFreeProduct) {
    return <Loading />;
  }
  return (
    <>
      <MetaData
        description="Subscribe & save with Prose's subscription service - The Salon! Save 15% on all orders and earn free products with every 10 items purchased, cancel anytime!"
        title="The Salon - Prose Subscription Service | Prose"
      />

      <BaseScene background="beige" display="block" header>
        <ProductSelectionSceneContainer>
          <GoBackButton to="/the-salon" />

          <Typography variant="h1">Choose your free product</Typography>

          <ProductGrid gridSize={gridSize}>
            {map(
              ({ slug, recommended }) => (
                <Product
                  key={`product-${slug}`}
                  onClick={() => setSelectedSlug(slug)}
                  recommended={recommended}
                  slug={slug}
                />
              ),
              selectableProducts
            )}
          </ProductGrid>

          <BasicModal isOpen={Boolean(selectedSlug)} onClose={() => setSelectedSlug(null)}>
            <ConnectedModalContent
              onClick={
                () =>
                  setSelectedProduct(selectedSlug).then(
                    () => navigate(location?.state?.referrer ?? '/the-salon') // Go back to referrer, default to the-salon
                  ) /* .catch() manage error here */
              }
              slug={selectedSlug}
            />
          </BasicModal>
        </ProductSelectionSceneContainer>
      </BaseScene>
    </>
  );
};

ProductSelection.propTypes = forbidExtraProps({
  status: PropTypes.oneOf([statuses.LOADING, statuses.SUCCESS, statuses.ERROR]).isRequired,
  canSelectFreeProduct: PropTypes.bool,
  setSelectedProduct: PropTypes.func.isRequired,
});

ProductSelection.defaultProps = {
  canSelectFreeProduct: false,
};

export default connect(
  state => ({
    status: membershipSelectors.getMembershipStatus(state),
    canSelectFreeProduct: membershipSelectors.canSelectFreeProduct(state),
  }),
  {
    setSelectedProduct: membershipActions.setSelectedProduct,
  }
)(ProductSelection);
